import { useEffect, useState } from 'react'
import { useRoute, Link } from 'wouter'
import { useQuery } from '@tanstack/react-query'
import { apiRequest } from '@/lib/utils'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ArrowLeft, Calendar, Link2, Search, Filter, Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { Skeleton } from '@/components/ui/skeleton'
import labella from 'labella'

export default function DashboardPage() {
  const [, params] = useRoute('/dashboard/:jobId')
  const jobId = params?.jobId

  const [searchTerm, setSearchTerm] = useState('')
  const [filterType, setFilterType] = useState('all')
  const [selectedDate, setSelectedDate] = useState(null)

  // Fetch job data
  const { data: jobData, isLoading } = useQuery({
    queryKey: ['job', jobId],
    queryFn: () => apiRequest(`/jobs/${jobId}`),
    enabled: !!jobId,
  })

  // Filter dates based on search and filter type
  const filteredDates = jobData?.dateList?.filter(date => {
    const matchesSearch = searchTerm === '' || 
      date.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      date.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      date.value?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesFilter = filterType === 'all' ||
      (filterType === 'anchor' && !date.relatedDates?.length) ||
      (filterType === 'calculated' && date.relatedDates?.length > 0) ||
      (filterType === 'resolved' && date.resolvedBy === 'relationship_analysis')

    return matchesSearch && matchesFilter
  }) || []

  // Get unique date types for the timeline
  const getDateCategory = (date) => {
    if (date.name.toLowerCase().includes('commencement') || date.name.toLowerCase().includes('execution')) return 'start'
    if (date.name.toLowerCase().includes('expiration') || date.name.toLowerCase().includes('expire') || date.name.toLowerCase().includes('end')) return 'end'
    if (date.name.toLowerCase().includes('renewal')) return 'renewal'
    if (date.name.toLowerCase().includes('payment') || date.name.toLowerCase().includes('rent')) return 'payment'
    return 'other'
  }

  // Color coding by category
  const getCategoryColor = (category) => {
    switch (category) {
      case 'start': return 'bg-green-500'
      case 'end': return 'bg-red-500'
      case 'renewal': return 'bg-blue-500'
      case 'payment': return 'bg-purple-500'
      default: return 'bg-gray-500'
    }
  }

  const getCategoryBadgeColor = (category) => {
    switch (category) {
      case 'start': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
      case 'end': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
      case 'renewal': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
      case 'payment': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
    }
  }

  // Parse dates for timeline
  const timelineDates = filteredDates
    .filter(date => {
      const val = date.value
      // Check if it's a parseable date (YYYY-MM-DD or Month DD, YYYY format)
      return val && (val.match(/^\d{4}-\d{2}-\d{2}$/) || val.match(/^[A-Za-z]+ \d{1,2}, \d{4}$/))
    })
    .map(date => ({
      ...date,
      parsedDate: new Date(date.value),
      category: getDateCategory(date)
    }))
    .sort((a, b) => a.parsedDate - b.parsedDate)

  if (isLoading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <Skeleton className="h-8 w-64 mb-4" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  if (!jobData) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Job not found</h1>
          <Link href="/">
            <Button>Go Home</Button>
          </Link>
        </div>
      </div>
    )
  }

  const totalDates = jobData.dateList?.length || 0
  const resolvedDates = jobData.dateList?.filter(d => d.resolvedBy === 'relationship_analysis').length || 0
  const relatedDates = jobData.dateList?.filter(d => d.relatedDates?.length > 0).length || 0

  return (
    <div className="container mx-auto px-4 py-8 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <Link href={`/results/${jobId}`}>
          <Button variant="ghost" className="mb-4" data-testid="button-back">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Results
          </Button>
        </Link>
        <h1 className="text-3xl font-bold mb-2" data-testid="text-dashboard-title">Lease Date Dashboard</h1>
        <p className="text-muted-foreground" data-testid="text-document-name">
          {jobData.fileName || 'Lease Document'}
        </p>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Total Dates</CardDescription>
            <CardTitle className="text-3xl" data-testid="text-total-dates">{totalDates}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Timeline Dates</CardDescription>
            <CardTitle className="text-3xl" data-testid="text-timeline-dates">{timelineDates.length}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>Related Dates</CardDescription>
            <CardTitle className="text-3xl" data-testid="text-related-dates">{relatedDates}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardDescription>AI Resolved</CardDescription>
            <CardTitle className="text-3xl" data-testid="text-resolved-dates">{resolvedDates}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Timeline Visualization */}
      {timelineDates.length > 0 && (
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Interactive Timeline
            </CardTitle>
            <CardDescription>
              Visual representation of key lease dates
            </CardDescription>
          </CardHeader>
          <CardContent>
            <TimelineVisualization dates={timelineDates} onDateClick={setSelectedDate} />
          </CardContent>
        </Card>
      )}

      {/* Filters and Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search dates by name, description, or value..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="input-search-dates"
              />
            </div>
            <div className="relative">
              <Filter className="absolute left-3 top-3 h-4 w-4 text-muted-foreground pointer-events-none" />
              <select
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
                className="flex h-10 w-full md:w-[200px] items-center justify-between rounded-md border border-input bg-background px-3 py-2 pl-10 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                data-testid="select-filter-type"
              >
                <option value="all">All Dates</option>
                <option value="anchor">Anchor Dates</option>
                <option value="calculated">Calculated Dates</option>
                <option value="resolved">AI Resolved</option>
              </select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Dates Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>All Dates ({filteredDates.length})</CardTitle>
              <CardDescription>Click on any date to see details and relationships</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {filteredDates.map((date, index) => {
              const category = getDateCategory(date)
              const isSelected = selectedDate?.name === date.name
              
              return (
                <div
                  key={index}
                  onClick={() => setSelectedDate(date)}
                  className={`p-4 border rounded-lg cursor-pointer transition-all hover:shadow-md ${
                    isSelected ? 'ring-2 ring-primary' : ''
                  }`}
                  data-testid={`card-date-${index}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div className={`w-3 h-3 rounded-full ${getCategoryColor(category)}`} />
                        <h3 className="font-semibold" data-testid={`text-date-name-${index}`}>{date.name}</h3>
                        {date.relatedDates && date.relatedDates.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            <Link2 className="w-3 h-3 mr-1" />
                            {date.relatedDates.length} related
                          </Badge>
                        )}
                      </div>
                      {date.description && (
                        <p className="text-sm text-muted-foreground mb-2" data-testid={`text-date-description-${index}`}>
                          {date.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-sm">
                        <span className="font-medium" data-testid={`text-date-value-${index}`}>{date.value}</span>
                        <Badge className={getCategoryBadgeColor(category)}>
                          {category}
                        </Badge>
                        {date.resolvedBy === 'relationship_analysis' && (
                          <Badge variant="secondary">AI Resolved</Badge>
                        )}
                      </div>
                      {date.relatedDates && date.relatedDates.length > 0 && (
                        <div className="mt-2 pt-2 border-t">
                          <p className="text-xs text-muted-foreground">
                            Related to: <span className="font-medium">{date.relatedDates.join(', ')}</span>
                          </p>
                          {date.relationshipType && (
                            <p className="text-xs text-muted-foreground">
                              Type: <span className="font-medium">{date.relationshipType}</span>
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
            {filteredDates.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                No dates match your search criteria
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

// Navigable Timeline with Smart Label Placement
function TimelineVisualization({ dates, onDateClick }) {
  const [hoveredDate, setHoveredDate] = useState(null)
  
  if (dates.length === 0) return null

  const minDate = new Date(Math.min(...dates.map(d => d.parsedDate)))
  const maxDate = new Date(Math.max(...dates.map(d => d.parsedDate)))
  
  // Initial view: first year from earliest date
  const oneYearLater = new Date(minDate)
  oneYearLater.setFullYear(minDate.getFullYear() + 1)
  
  const [viewStart, setViewStart] = useState(minDate)
  const [viewEnd, setViewEnd] = useState(oneYearLater)
  const [selectedCategory, setSelectedCategory] = useState(null) // For legend filtering
  
  // Filter dates to only show those in current view and selected category
  const visibleDates = dates.filter(d => {
    const inTimeRange = d.parsedDate >= viewStart && d.parsedDate <= viewEnd
    const matchesCategory = !selectedCategory || d.category === selectedCategory
    return inTimeRange && matchesCategory
  })
  
  // Calculate position based on current view window
  const viewDuration = viewEnd - viewStart
  const TIMELINE_WIDTH = 1400 // Increased width for better spacing
  const PADDING_LEFT = 20 // Left padding to prevent edge clipping
  const LABEL_WIDTH = 140 // Estimated label width
  const LABEL_HEIGHT = 50 // Height per label row
  const TIMELINE_TOP = 100 // Timeline bar position from top
  
  const getPosition = (dateValue) => {
    const offset = dateValue - viewStart
    return PADDING_LEFT + (offset / viewDuration) * TIMELINE_WIDTH
  }

  const getCategoryColor = (category) => {
    switch (category) {
      case 'start': return 'bg-green-500'
      case 'end': return 'bg-red-500'
      case 'renewal': return 'bg-blue-500'
      case 'payment': return 'bg-purple-500'
      default: return 'bg-gray-500'
    }
  }

  // Labella.js smart label placement (Twitter's algorithm)
  const calculateLabelPositions = () => {
    if (visibleDates.length === 0) return []
    
    // Create Labella nodes for each date
    const nodes = visibleDates.map(date => {
      const position = getPosition(date.parsedDate)
      // Each node has: idealPos (where we want it), width (label size), and data
      return new labella.Node(position, LABEL_WIDTH, date)
    })
    
    // Use Labella's Force to compute optimal positions
    const force = new labella.Force({
      minPos: PADDING_LEFT,
      maxPos: TIMELINE_WIDTH + PADDING_LEFT
    })
      .nodes(nodes)
      .compute()
    
    // Use Labella's Renderer to get layered layout
    const renderer = new labella.Renderer({
      layerGap: LABEL_HEIGHT,
      nodeHeight: 30,
      direction: 'down' // Labels go down from timeline
    })
    
    renderer.layout(force.nodes())
    
    // Convert Labella's output to our format
    return force.nodes().map(node => ({
      date: node.data,
      x: node.currentPos, // Labella's computed position (may differ from ideal to avoid overlap)
      yOffset: node.layerIndex || 0,
      layer: node.layer
    }))
  }

  const labelPositions = calculateLabelPositions()

  const goToPreviousYear = () => {
    const newStart = new Date(viewStart)
    newStart.setFullYear(newStart.getFullYear() - 1)
    const newEnd = new Date(viewEnd)
    newEnd.setFullYear(newEnd.getFullYear() - 1)
    
    if (newStart >= minDate) {
      setViewStart(newStart)
      setViewEnd(newEnd)
    }
  }

  const goToNextYear = () => {
    const newStart = new Date(viewStart)
    newStart.setFullYear(newStart.getFullYear() + 1)
    const newEnd = new Date(viewEnd)
    newEnd.setFullYear(newEnd.getFullYear() + 1)
    
    if (newEnd <= maxDate) {
      setViewStart(newStart)
      setViewEnd(newEnd)
    }
  }

  const canGoPrevious = viewStart > minDate
  const canGoNext = viewEnd < maxDate

  // Generate year and month tick marks
  const yearTicks = []
  const monthTicks = []
  const current = new Date(viewStart)
  current.setDate(1)
  current.setHours(0, 0, 0, 0)
  
  while (current <= viewEnd) {
    const position = getPosition(current)
    
    if (current.getMonth() === 0) {
      // January 1st = Year tick
      yearTicks.push({
        year: current.getFullYear(),
        position
      })
    } else {
      // Other months = Month tick
      monthTicks.push({ position })
    }
    
    current.setMonth(current.getMonth() + 1)
  }

  // Calculate dynamic height based on max label offset
  const maxLabelOffset = Math.max(...labelPositions.map(l => l.yOffset), 0)
  const timelineHeight = TIMELINE_TOP + (maxLabelOffset + 1) * LABEL_HEIGHT + 100

  return (
    <div className="relative" data-testid="timeline-visualization">
      {/* Navigation Controls */}
      <div className="flex items-center justify-between mb-6">
        <Button
          onClick={goToPreviousYear}
          disabled={!canGoPrevious}
          variant="outline"
          size="sm"
          data-testid="button-previous-year"
        >
          <ChevronLeft className="h-4 w-4 mr-1" />
          Previous Year
        </Button>
        
        <div className="text-sm text-muted-foreground">
          Viewing: {viewStart.toLocaleDateString()} - {viewEnd.toLocaleDateString()}
          <span className="ml-2 text-xs">
            ({visibleDates.length} dates in view, {dates.length} total)
          </span>
        </div>
        
        <Button
          onClick={goToNextYear}
          disabled={!canGoNext}
          variant="outline"
          size="sm"
          data-testid="button-next-year"
        >
          Next Year
          <ChevronRight className="h-4 w-4 ml-1" />
        </Button>
      </div>

      {/* Timeline Container */}
      <div className="overflow-x-auto pb-4" style={{ maxWidth: '100%' }}>
        <div className="relative" style={{ width: `${TIMELINE_WIDTH + 40}px`, height: `${timelineHeight}px`, paddingLeft: '20px', paddingRight: '20px' }}>
          
          {/* Year tick marks (large, labeled) */}
          {yearTicks.map((tick, i) => (
            <div
              key={`year-${i}`}
              className="absolute border-l-2 border-gray-500 dark:border-gray-400"
              style={{ 
                left: `${tick.position}px`,
                top: `${TIMELINE_TOP - 20}px`,
                height: '40px'
              }}
            >
              <div className="text-sm font-semibold text-foreground -ml-4 -mt-5">
                {tick.year}
              </div>
            </div>
          ))}

          {/* Month tick marks (small, unlabeled) */}
          {monthTicks.map((tick, i) => (
            <div
              key={`month-${i}`}
              className="absolute border-l border-gray-300 dark:border-gray-600"
              style={{ 
                left: `${tick.position}px`,
                top: `${TIMELINE_TOP - 10}px`,
                height: '20px'
              }}
            />
          ))}

          {/* Timeline bar */}
          <div 
            className="absolute h-2 bg-gray-200 dark:bg-gray-700 rounded-full" 
            style={{ 
              left: `${PADDING_LEFT}px`,
              width: `${TIMELINE_WIDTH}px`,
              top: `${TIMELINE_TOP}px` 
            }}
          />
          
          {/* Connecting lines - rendered first (behind dots) */}
          {labelPositions.map((labelPos, index) => {
            const { date, x, yOffset } = labelPos
            const dotPosition = getPosition(date.parsedDate)
            const topPosition = TIMELINE_TOP + (yOffset + 1) * LABEL_HEIGHT + 10
            
            return (
              <div key={`line-${index}`}>
                {/* Vertical line from dot to label */}
                <div 
                  className="absolute w-0.5 bg-gray-400 dark:bg-gray-500"
                  style={{ 
                    left: `${dotPosition}px`,
                    top: `${TIMELINE_TOP + 8}px`,
                    height: `${topPosition - TIMELINE_TOP - 8}px`
                  }}
                />
                
                {/* Horizontal connector if label was moved horizontally */}
                {Math.abs(x - dotPosition) > 2 && (
                  <div 
                    className="absolute h-0.5 bg-gray-400 dark:bg-gray-500"
                    style={{ 
                      left: `${Math.min(dotPosition, x)}px`,
                      top: `${topPosition}px`,
                      width: `${Math.abs(x - dotPosition)}px`
                    }}
                  />
                )}
              </div>
            )
          })}

          {/* Labels */}
          {labelPositions.map((labelPos, index) => {
            const { date, x, yOffset } = labelPos
            const topPosition = TIMELINE_TOP + (yOffset + 1) * LABEL_HEIGHT + 10
            
            return (
              <div 
                key={`label-${index}`}
                className="absolute"
                style={{ 
                  left: `${x - LABEL_WIDTH / 2}px`,
                  top: `${topPosition}px`,
                  width: `${LABEL_WIDTH}px`
                }}
              >
                <div className="text-xs font-medium text-foreground truncate px-1 text-center">
                  {date.name}
                </div>
              </div>
            )
          })}

          {/* Dot markers - rendered last (on top) */}
          {labelPositions.map((labelPos, index) => {
            const { date } = labelPos
            const dotPosition = getPosition(date.parsedDate)
            
            return (
              <div
                key={`dot-${index}`}
                onClick={() => onDateClick(date)}
                onMouseEnter={() => setHoveredDate(date)}
                onMouseLeave={() => setHoveredDate(null)}
                className={`absolute w-4 h-4 rounded-full cursor-pointer transition-all hover:scale-150 ${getCategoryColor(date.category)} shadow-lg`}
                style={{ 
                  left: `${dotPosition - 8}px`,
                  top: `${TIMELINE_TOP}px`,
                  zIndex: 10
                }}
                data-testid={`timeline-marker-${index}`}
              />
            )
          })}

          {/* Enhanced Tooltip on Hover */}
          {hoveredDate && (
            <div className="absolute left-1/2 top-0 -translate-x-1/2 bg-popover text-popover-foreground px-4 py-3 rounded-lg shadow-xl border border-border max-w-sm z-50">
              <div className="font-semibold mb-1">{hoveredDate.name}</div>
              <div className="text-sm text-muted-foreground mb-1">{hoveredDate.value}</div>
              {hoveredDate.description && (
                <div className="text-xs text-muted-foreground mt-1 border-t border-border pt-1">
                  {hoveredDate.description}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Timeline range info */}
      <div className="text-xs text-center text-muted-foreground mt-4">
        Full range: {minDate.toLocaleDateString()} - {maxDate.toLocaleDateString()}
      </div>

      {/* Legend - Clickable filters */}
      <div className="flex flex-wrap gap-4 mt-6 justify-center">
        <button
          onClick={() => setSelectedCategory(selectedCategory === 'start' ? null : 'start')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all cursor-pointer hover:bg-accent ${
            selectedCategory === 'start' ? 'bg-accent ring-2 ring-green-500' : ''
          }`}
          data-testid="legend-start"
        >
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm">Start Dates</span>
        </button>
        <button
          onClick={() => setSelectedCategory(selectedCategory === 'end' ? null : 'end')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all cursor-pointer hover:bg-accent ${
            selectedCategory === 'end' ? 'bg-accent ring-2 ring-red-500' : ''
          }`}
          data-testid="legend-end"
        >
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-sm">End Dates</span>
        </button>
        <button
          onClick={() => setSelectedCategory(selectedCategory === 'renewal' ? null : 'renewal')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all cursor-pointer hover:bg-accent ${
            selectedCategory === 'renewal' ? 'bg-accent ring-2 ring-blue-500' : ''
          }`}
          data-testid="legend-renewal"
        >
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-sm">Renewals</span>
        </button>
        <button
          onClick={() => setSelectedCategory(selectedCategory === 'payment' ? null : 'payment')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all cursor-pointer hover:bg-accent ${
            selectedCategory === 'payment' ? 'bg-accent ring-2 ring-purple-500' : ''
          }`}
          data-testid="legend-payment"
        >
          <div className="w-3 h-3 rounded-full bg-purple-500" />
          <span className="text-sm">Payments</span>
        </button>
        <button
          onClick={() => setSelectedCategory(selectedCategory === 'other' ? null : 'other')}
          className={`flex items-center gap-2 px-3 py-1.5 rounded-md transition-all cursor-pointer hover:bg-accent ${
            selectedCategory === 'other' ? 'bg-accent ring-2 ring-gray-500' : ''
          }`}
          data-testid="legend-other"
        >
          <div className="w-3 h-3 rounded-full bg-gray-500" />
          <span className="text-sm">Other</span>
        </button>
      </div>
    </div>
  )
}
